import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import FinalCTA from "@/components/FinalCTA";
import {
  Network, Shield, Users, GraduationCap, Handshake, FlaskConical,
  ArrowRight, ArrowUpRight, Search, Sparkles, BadgeCheck,
  LifeBuoy, Briefcase, Globe2, Layers, Star
} from "lucide-react";
import {
  ALLIANCE_NETWORK, PARTNERS, FILTER_CATEGORIES, FEATURED_SLUGS, getPartnerBySlug
} from "@/data/partners";

const ICONS = { Network, Shield, Users, GraduationCap, Handshake, FlaskConical };

function Wordmark({ logo, size = "md" }) {
  if (!logo || logo.type !== "wordmark") return null;
  const dim = size === "lg" ? "h-16 w-16 text-2xl" : size === "sm" ? "h-9 w-9 text-xs" : "h-12 w-12 text-base";
  return (
    <div className={`${dim} rounded-xl bg-gradient-to-br ${logo.gradient} text-white grid place-items-center font-display font-extrabold tracking-tight shadow-lg shadow-blue-500/10`}>
      {logo.letters}
    </div>
  );
}

function PartnerCard({ p, featured = false }) {
  return (
    <Link
      to={`/alliances/${p.slug}`}
      data-testid={`alliance-partner-card-${p.slug}`}
      className={`group relative flex flex-col bg-white border border-slate-200 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#3B82F6] hover:shadow-[0_24px_60px_-26px_rgba(59,130,246,0.45)] overflow-hidden ${featured ? "lg:p-7" : ""}`}
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:via-purple-500/10 group-hover:to-cyan-500/10 transition-colors blur-2xl" />
      <div className="relative flex items-start gap-4">
        <Wordmark logo={p.logo} size={featured ? "lg" : "md"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-display font-extrabold text-[#0A1B5E] tracking-tight ${featured ? "text-2xl" : "text-lg"}`}>{p.name}</h3>
            {p.featured && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech tracking-widest uppercase bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-100">
                <Star size={10} /> Strategic
              </span>
            )}
          </div>
          <span className="mt-1 inline-block font-mono-tech text-[10px] tracking-widest uppercase text-[#3B82F6] bg-blue-50 px-2 py-1 rounded-full border border-blue-100">
            {p.category}
          </span>
        </div>
      </div>
      <p className={`relative mt-5 text-slate-600 leading-relaxed ${featured ? "text-[15px]" : "text-sm"}`}>{p.tagline}</p>
      <div className="relative mt-4 pt-4 border-t border-slate-100">
        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-400">Key Use Case</div>
        <div className="mt-1 text-sm text-slate-700 line-clamp-2">{p.useCases[0]}</div>
      </div>
      <span className="relative mt-5 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
        Learn more <ArrowRight size={14} />
      </span>
    </Link>
  );
}

const STATS = [
  { value: "11+", label: "Technology Partners", icon: Handshake },
  { value: "10", label: "Security Categories", icon: Layers },
  { value: "30+", label: "Enterprise Solutions", icon: Briefcase },
  { value: "Pan-India", label: "Deployment Coverage", icon: Globe2 },
];

const TRUST = [
  { icon: Shield, title: "Enterprise Security", desc: "Endpoint, network, identity and data — engineered for regulated enterprises." },
  { icon: BadgeCheck, title: "Compliance Readiness", desc: "DPDP, ISO 27001 and SOC 2 — alliances pre-mapped to controls." },
  { icon: Briefcase, title: "Managed Services", desc: "24/7 SOC and operations across the alliance technology stack." },
  { icon: GraduationCap, title: "Workforce Security", desc: "Skilled talent ready to deploy and operate every partner platform." },
  { icon: LifeBuoy, title: "Digital Resilience", desc: "Layered controls, response readiness and continuous improvement." },
];

export default function Alliances() {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const featuredPartners = FEATURED_SLUGS.map(getPartnerBySlug).filter(Boolean);
  const otherPartners = PARTNERS.filter((p) => !p.featured);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return otherPartners.filter((p) => {
      const matchesCat = filter === "All" || p.category === filter;
      const matchesQuery = !q ||
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [filter, query, otherPartners]);

  return (
    <div data-testid="alliances-page">
      {/* HERO — dark, futuristic, cyber network */}
      <section data-testid="alliances-hero" className="relative overflow-hidden pt-32 pb-24 text-white">
        <div className="absolute inset-0 dark-strip-bg" />
        <div className="absolute inset-0 opacity-25" style={{
          backgroundImage:
            "linear-gradient(rgba(34,211,238,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.18) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }} />
        <div className="absolute -top-24 right-10 w-[420px] h-[420px] rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute bottom-0 left-10 w-[460px] h-[460px] rounded-full bg-purple-500/20 blur-3xl" />

        <div className="relative container-cd">
          <div className="max-w-4xl">
            <span className="badge-cd !bg-white/10 !text-cyan-300 !border-white/15">Strategic Alliance Ecosystem</span>
            <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Strategic Technology <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text text-transparent">
                Alliance Ecosystem
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              Partnering with global cybersecurity leaders to deliver enterprise-grade protection, compliance, resilience and digital trust.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact?type=partnership" className="btn-accent" data-testid="alliances-hero-cta">
                Partner with Cosmos Defence <ArrowUpRight size={16} />
              </Link>
              <a href="#alliance-partners" className="btn-ghost !text-white !border-white/25 hover:!bg-white/10 hover:!text-white" data-testid="alliances-explore-cta">
                Explore the Ecosystem
              </a>
            </div>
          </div>

          {/* Hero stats strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <div key={s.label} data-testid={`alliances-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6">
                <s.icon size={20} className="text-cyan-300" />
                <div className="mt-4 font-mono-tech text-3xl md:text-4xl font-bold bg-gradient-to-br from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="mt-1 text-xs font-mono-tech tracking-[0.18em] uppercase text-slate-300">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Strategic Alliances */}
      <section data-testid="featured-alliances-section" className="py-20 bg-white">
        <div className="container-cd">
          <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
            <div className="max-w-2xl">
              <span className="badge-cd">Featured Strategic Alliances</span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
                Global leaders. Indian outcomes.
              </h2>
              <p className="mt-3 text-slate-600 max-w-xl">
                The most recognised names in cybersecurity and enterprise infrastructure — operationalised by Cosmos Defence.
              </p>
            </div>
            <Sparkles size={28} className="text-amber-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredPartners.map((p) => (
              <PartnerCard key={p.slug} p={p} featured />
            ))}
          </div>
        </div>
      </section>

      {/* Alliance Network Architecture — six layers (compact) */}
      <section data-testid="alliance-network-section" className="py-20 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative container-cd">
          <div className="text-center max-w-3xl mx-auto">
            <span className="badge-cd">Alliance Network Architecture</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
              Six alliance layers · one operating ecosystem
            </h2>
            <p className="mt-3 text-slate-600">Inspired by the partner ecosystems of global cybersecurity platforms — adapted for the realities of the Indian enterprise.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ALLIANCE_NETWORK.map((layer, i) => {
              const Icon = ICONS[layer.icon] || Network;
              return (
                <div key={layer.id} data-testid={`alliance-layer-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:border-[#3B82F6] hover:shadow-[0_22px_50px_-24px_rgba(10,27,94,0.25)] transition-all">
                  <div className={`h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br ${layer.accent} text-white shadow-lg shadow-blue-500/10`}>
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-[#0A1B5E]">{layer.title}</h3>
                  <div className="text-xs text-slate-500 mt-1">{layer.role}</div>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">{layer.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Alliance Partners — filters + search + 4-col grid */}
      <section id="alliance-partners" data-testid="alliance-partners-section" className="py-20 bg-white">
        <div className="container-cd">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <span className="badge-cd">All Alliance Partners</span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
                The full alliance ecosystem
              </h2>
              <p className="mt-3 text-slate-600">Every partner is selected for real-world deployment fit, India-grade support and operational maturity.</p>
            </div>
            <div className="relative w-full lg:w-80" data-testid="alliance-search-wrap">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search partners, categories…"
                data-testid="alliance-search-input"
                className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none font-display text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="alliance-filter-chips">
            {FILTER_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`alliance-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-xs font-display font-semibold border transition-all whitespace-nowrap ${
                  filter === c
                    ? "bg-[#0A1B5E] text-white border-[#0A1B5E]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#3B82F6] hover:text-[#3B82F6]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filtered.map((p) => (
                <PartnerCard key={p.slug} p={p} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500" data-testid="alliance-empty">
              No partners match the current filter. Try a different category or clear the search.
            </div>
          )}
        </div>
      </section>

      {/* Logo Wall */}
      <section data-testid="alliance-logo-wall" className="py-14 bg-[#F8FAFC] border-y border-slate-100">
        <div className="container-cd text-center mb-8">
          <span className="badge-cd">Alliance Logo Wall</span>
        </div>
        <Marquee gradient gradientColor="#F8FAFC" gradientWidth={120} speed={36} pauseOnHover>
          {PARTNERS.map((p) => (
            <Link
              key={p.slug}
              to={`/alliances/${p.slug}`}
              data-testid={`alliance-logo-wall-${p.slug}`}
              className="mx-3 px-5 py-4 rounded-2xl bg-white border border-slate-200 hover:border-[#3B82F6] hover:shadow-[0_14px_40px_-18px_rgba(59,130,246,0.5)] transition-all inline-flex items-center gap-3 group"
            >
              <Wordmark logo={p.logo} size="sm" />
              <div className="text-left">
                <div className="font-display font-bold text-[#0A1B5E] tracking-tight text-sm leading-tight">{p.name}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-slate-400 mt-0.5">{p.category}</div>
              </div>
            </Link>
          ))}
        </Marquee>
      </section>

      {/* Trust Section */}
      <section data-testid="alliance-trust-section" className="py-20 bg-white">
        <div className="container-cd">
          <div className="text-center max-w-3xl mx-auto">
            <span className="badge-cd">Trust Architecture</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
              Trusted Technologies. Proven Outcomes.
            </h2>
            <p className="mt-3 text-slate-600">A single ecosystem delivering enterprise security, compliance, managed operations, workforce readiness and digital resilience.</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {TRUST.map((t, i) => (
              <div key={t.title} data-testid={`alliance-trust-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:border-[#3B82F6] hover:shadow-[0_22px_50px_-24px_rgba(10,27,94,0.25)] transition-all">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-blue-50 border border-blue-100 text-[#3B82F6]">
                  <t.icon size={20} strokeWidth={1.6} />
                </span>
                <h3 className="mt-4 font-display text-base font-bold text-[#0A1B5E]">{t.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
